import React, { useState, useEffect, useRef } from "react";
import CustomModal from "../../../../Components/Modals/CustomModal";
import DeleteIcon from '../../../../../../src/designUtils/ProjectDashboard/DeleteIcon.svg';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";
function get(obj, path, fallback) {
  var result = obj;
  var parts = path.split(".");
  for (var i = 0; i < parts.length; i++) {
    if (result === null || result === undefined) return fallback;
    result = result[parts[i]];
  }
  return result === undefined || result === null ? fallback : result;
}

function cleanHtmlTags(val) {
  if (typeof val === "string") {
    return val.replace(/<\/?p>/gi, " ").trim();
  }
  if (Array.isArray(val)) {
    return val.map(item => cleanHtmlTags(item));
  }
  if (val !== null && typeof val === "object") {
    const cleaned = {};
    for (let k in val) cleaned[k] = cleanHtmlTags(val[k]);
    return cleaned;
  }
  return val;
}

function getInitialValue(val) {
  if (val === null || val === undefined) return "";
  return cleanHtmlTags(val);
}

function isPassthroughWrapperSchema(schema) {
  if (!schema) return false;
  if (schema.type === "array" && schema.items) {
    return isPassthroughWrapperSchema(schema.items);
  }
  if (schema.type === "object" && schema.properties) {
    const visibleKeys = Object.keys(schema.properties).filter(
      (k) =>
        k !== "marketplace_id" &&
        k !== "language_tag" &&
        !schema.properties[k].hidden
    );
    return visibleKeys.length === 1;
  }
  return false;
}
function getAllowedValuesForKey(itemSchema, key) {
  const prop = itemSchema && itemSchema.properties && itemSchema.properties[key];
  if (!prop || !Array.isArray(prop.enum)) return null;
  return prop.enum;
}
function filterAudienceEntries(val, nestedSchema) {
  if (!Array.isArray(val) || !nestedSchema) return val;
  const selectors = get(nestedSchema, "selectors", []);
  if (!selectors.includes("audience")) return val;

  const allowedAudiences = getAllowedValuesForKey(get(nestedSchema, "items", {}), "audience");
  if (!allowedAudiences) return val;

  return val.filter(
    (item) => !(item && item.audience !== undefined && !allowedAudiences.includes(item.audience))
  );
}
function setDeep(obj, path, newValue) {
  const root = Array.isArray(obj) ? [...obj] : { ...obj };
  let current = root;
  for (let i = 0; i < path.length - 1; i++) {
    const key = path[i];
    const nextKey = path[i + 1];
    const nextIsIndex = typeof nextKey === "number";

    if (Array.isArray(current[key])) {
      current[key] = [...current[key]];
    } else if (current[key] && typeof current[key] === "object") {
      current[key] = { ...current[key] };
    } else {
      // key didn't exist yet (undefined) —
      // seed as array if next segment is a numeric index, else as object
      current[key] = nextIsIndex ? [] : {};
    }
    current = current[key];
  }
  current[path[path.length - 1]] = newValue;
  return root;
}
function buildEmptyStub(schema) {
  if (!schema) return "";
  if (["string", "number", "integer", "boolean"].includes(schema.type)) {
    return "";
  }
  if (schema.type === "array") {
    return [buildEmptyStub(schema.items)];
  }
  if (schema.type === "object" || schema.properties) {
    const props = schema.properties || {};
    const obj = {};
    Object.keys(props).forEach((k) => {
      if (k === "marketplace_id" || k === "language_tag") return;
      obj[k] = buildEmptyStub(props[k]);
    });
    return obj;
  }
  return "";
}
// ============================================================
//Deprecated values warning badge
// ============================================================
function DeprecatedWarningBadge({ value, deprecatedValues }) {
  if (!deprecatedValues || !deprecatedValues.length) return null;
  const isDeprecated = deprecatedValues.some(
    (d) => String(d).toLowerCase() === String(value || "").toLowerCase()
  );
  if (!isDeprecated) return null;
  return (
    <div style={{
      display: "flex",
      alignItems: "center",
      gap: 4,
      marginTop: 3,
      fontSize: 11,
      color: "#b45309",
      background: "#fffbeb",
      border: "1px solid #fcd34d",
      borderRadius: 4,
      padding: "2px 6px"
    }}>
      <span>⚠️</span>
      <span>
        This value is <strong>deprecated</strong> by Amazon. Please select an updated value.
      </span>
    </div>
  );
}

// ============================================================
// Pattern / URL inline validation hint
// ============================================================
function PatternValidationHint({ value, rules }) {
  if (!value || typeof value !== "string" || value.trim() === "") return null;
  const pattern = rules && rules.pattern;
  const format = rules && rules.format;

  if (pattern) {
    try {
      if (!new RegExp(pattern).test(value)) {
        return (
          <small style={{ color: "#dc2626", fontSize: 11, display: "block", marginTop: 2 }}>
            {format === "uri"
              ? "Invalid URL – must start with https:// or s3://"
              : `Invalid format (pattern: ${pattern})`}
          </small>
        );
      }
    } catch (e) { /* invalid regex – skip */ }
  } else if (format === "uri") {
    try {
      const u = new URL(value);
      if (!u.protocol || !u.host) throw new Error();
    } catch {
      return (
        <small style={{ color: "#dc2626", fontSize: 11, display: "block", marginTop: 2 }}>
          Invalid URL format
        </small>
      );
    }
  }
  return null;
}

const AutoResizeTextarea = ({ value, onChange, readOnly, style, placeholder }) => {
  const textareaRef = useRef(null);
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      const extraRowHeight = 18;
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight + extraRowHeight}px`;
    }
  }, [value]);
  return (
    <textarea
      ref={textareaRef}
      style={{ ...style, resize: "none", overflow: "hidden", boxSizing: "border-box", fontFamily: "inherit", lineHeight: "1.4" }}
      value={value}
      onChange={onChange}
      readOnly={readOnly}
      placeholder={placeholder}
      rows={1}
    />
  );
};
function toReadableLabel(str) {
  if (!str) return str;
  return str
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}
function isRedundantTitle(candidate, baseTitle) {
  if (!candidate || !baseTitle) return false;
  const normalize = (s) => String(s).replace(/^item\s+/i, "").trim().toLowerCase();
  return normalize(candidate) === normalize(baseTitle);
}
function getSummary(val) {
  if (!val || typeof val !== "object") return val || "—";
  const obj = Array.isArray(val) ? val[0] : val;
  if (!obj || typeof obj !== "object") return String(val);
  return Object.entries(obj)
    .filter(([k]) => !["marketplace_id", "language_tag"].includes(k))
    .map(([, v]) => {
      if (v === null || v === undefined || v === "") return null;
      if (typeof v === "object") return null;
      return String(v);
    })
    .filter(Boolean)
    .slice(0, 4)
    .join(" ") || "—";
}

function renderModalColValue(val, schema = {}, topLabel = "", parentLabel = "") {
  if (val === null || val === undefined) return <span style={{ color: "#9ca3af" }}>—</span>;

  if (typeof val === "string") {
    return <span>{val.replace(/<\/?p>/gi, " ").trim()}</span>;
  }

  if (Array.isArray(val)) {
    if (val.length === 0) return <span style={{ color: "#9ca3af" }}>—</span>;
    const itemSchema = schema.items || schema;
    const selectors = schema.selectors || [];

    const varyingSelectors = selectors.filter((s) => {
      const seen = new Set(
        val
          .filter((item) => item && typeof item === "object" && item[s] !== undefined && item[s] !== null && item[s] !== "")
          .map((item) => String(item[s]))
      );
      return seen.size > 1;
    });

    const isSelectorArray = selectors.length > 0;
    return val.map((item, i) => {
      const selectorLabel = (varyingSelectors.length > 0 && item && typeof item === "object")
        ? varyingSelectors
            .map((s) => {
              const propSchema = get(itemSchema, `properties.${s}`, {});
              const idx = Array.isArray(propSchema.enum) ? propSchema.enum.indexOf(item[s]) : -1;
              const displayVal = idx !== -1 && propSchema.enumNames ? propSchema.enumNames[idx] : item[s];
              return `${toReadableLabel(s)}: ${displayVal}`;
            })
            .join("  ·  ")
        : null;
      return (
        <div
          key={i}
          style={{
            marginBottom: isSelectorArray ? 16 : 8,
            paddingTop: isSelectorArray && i > 0 ? 12 : 0,
            borderTop: isSelectorArray && i > 0 ? "1px solid #e5e7eb" : "none"
          }}
        >
          {selectorLabel && (
            <div style={{ fontSize: 11, fontWeight: 700, color: "#111827", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.03em" }}>
              {selectorLabel}
            </div>
          )}
          {renderModalColValue(item, itemSchema, topLabel, parentLabel)}
        </div>
      );
    });
  }

  if (typeof val === "object") {
    const props = schema.properties || (schema.items && schema.items.properties) || {};

    if (props.value !== undefined && props.unit !== undefined) {
      const v = val.value;
      const u = val.unit;
      const displayV = (v === null || v === undefined || v === "")
        ? "—"
        : (typeof v === "string" ? v.replace(/<\/?p>/gi, " ").trim() : String(v));
      return <span>{displayV}{u ? ` ${u}` : ""}</span>;
    }

    const stripPrefix = (text, prefix) => {
      if (!prefix) return text;
      const escaped = prefix.trim().replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      return text.replace(new RegExp(`^${escaped}\\s+`, "i"), "");
    };

    const orderedKeys = Object.keys(props).length > 0 ? Object.keys(props) : Object.keys(val);
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 2, marginLeft: 4 }}>
        {orderedKeys
          .filter((k) => k !== "marketplace_id" && k !== "language_tag")
          .map((k) => {
            const propSchema = props[k] || {};
            if (propSchema.hidden) return null;
            const v = val[k];
            if (v === undefined) return null;

            // Nested single-child wrapper (e.g. "schedule") already inside a
            // labeled field adds no new info — skip its label, flatten through.
            const isWrapper = isPassthroughWrapperSchema(propSchema);
            if (isWrapper && parentLabel) {
              return (
                <React.Fragment key={k}>
                  {renderModalColValue(v, propSchema, topLabel, parentLabel)}
                </React.Fragment>
              );
            }

            const rawLabel = propSchema.title || toReadableLabel(k);
            const label = stripPrefix(stripPrefix(rawLabel, topLabel), parentLabel);

            return (
              <div key={k} style={{ fontSize: 11 }}>
                <b style={{ color: "#555", marginRight: 4 }}>{label}:</b>
                {typeof v === "object" && v !== null ? (
                  <div style={{ paddingLeft: 8, borderLeft: "1px solid #ccc", margin: "2px 0" }}>
                    {renderModalColValue(v, propSchema, topLabel, label)}
                  </div>
                ) : (
                  <span>{typeof v === "string" ? v.replace(/<\/?p>/gi, " ").trim() : String(v)}</span>
                )}
              </div>
            );
          })}
      </div>
    );
  }

  return <span>{String(val)}</span>;
}

function AmazonProductListingEditableField({ field = {}, onFieldChange, changedValue, isUpdated, fieldLabel, readOnly }) {
  // ============================================================
  // default_value prefill when normalized_value is empty
  // ============================================================
  var rawValue = isUpdated ? changedValue : get(field, "normalized_value", null);
  if (rawValue === null || rawValue === undefined || rawValue === "") {
    const defaultVal = get(field, "schema.default_value", null);
    if (defaultVal !== null && defaultVal !== undefined) rawValue = defaultVal;
  }
  const _isNestedObject = get(field, "render.component", "") === "NESTED_OBJECT";

  if (_isNestedObject && (rawValue !== null && rawValue !== undefined && rawValue !== "") && !Array.isArray(rawValue) && typeof rawValue !== "object") {
    const itemProps = get(field, "schema.nested_schema.items.properties", null);
    if (itemProps) {
      const structuredObj = {};
      Object.keys(itemProps).forEach(k => {
        if (k === "marketplace_id" || k === "language_tag") return;
        const propSchema = itemProps[k];
        if (k === "value" || propSchema.type === "number" || propSchema.type === "integer" || propSchema.type === "string") {
          structuredObj[k] = k === "value" ? rawValue : "";
        } else if (propSchema.type === "object" && propSchema.properties) {
          const childObj = {};
          Object.keys(propSchema.properties).forEach(ck => {
            if (ck !== "marketplace_id" && ck !== "language_tag") childObj[ck] = "";
          });
          structuredObj[k] = childObj;
        } else {
          structuredObj[k] = "";
        }
      });
      rawValue = [structuredObj];
    }
  }
  if (_isNestedObject && rawValue !== null && rawValue !== undefined && rawValue !== "" && !Array.isArray(rawValue) && typeof rawValue === "object") {
    rawValue = [rawValue];
  }
  if ((rawValue === null || rawValue === undefined || rawValue === "") && _isNestedObject) {
    const itemProps = get(field, "schema.nested_schema.items.properties", null);
    if (itemProps) {
      const emptyObj = {};
      Object.keys(itemProps).forEach(k => {
        if (k === "marketplace_id" || k === "language_tag") return;
        emptyObj[k] = buildEmptyStub(itemProps[k]);
      });
      rawValue = [emptyObj];
    }
  }
  var initial = getInitialValue(rawValue);

  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showNestedModal, setShowNestedModal] = useState(false);
  const [modalInitialValue, setModalInitialValue] = useState(null);
  var [value, setValue] = useState(initial);

  var options = get(field, "schema.allowed_values", []) || [];
  var allowCustomValues = get(field, "schema.rules.allow_custom_values", false);
  const component = get(field, "render.component", "TEXT_INPUT");
  const isDropdown = component === "SELECT";
  const isToggle = component === "TOGGLE";
  const isNestedObject = component === "NESTED_OBJECT";
  var rules = get(field, "schema.rules", {}) || {};
  const isArrayField = rules.field_type === "array";
  const isMultiValueField =
    isArrayField &&
    (
      (rules.maxItems && rules.maxItems > 1) ||
      (rules.maxUniqueItems && rules.maxUniqueItems > 1)
    );
  // Type A: single value suggestive (maxUniqueItems=1, allow_custom_values=true)
  var isSuggestiveField = options.length > 0 && allowCustomValues && !isMultiValueField;
  // Type B: multi-value suggestive (maxUniqueItems>1, allow_custom_values=true) 
  var isSuggestiveMultiField = options.length > 0 && allowCustomValues && isMultiValueField;
  var isPlainMultiTextField = component === "TEXT_INPUT" && isMultiValueField && options.length === 0;
  var isEditable = get(field, "schema.editable", true);
  const isOfferGroupField = get(field, "ui.subcategory", "") === "Offer";
  var examples = get(field, "schema.examples", []) || [];
  var fieldType = rules.type;
  var actualFieldType = fieldType;
  var maxLength = actualFieldType === "string" ? rules.maxLength : null;
  var maxUtf8ByteLength = rules.maxUtf8ByteLength;
  var minLength = rules.minLength;
  var minimum = rules.minimum;
  var maximum = rules.maximum;
  var exclusiveMinimum = rules.exclusiveMinimum;
  var exclusiveMaximum = rules.exclusiveMaximum;
  var multipleOf = rules.multipleOf;
  var decimalPlaces = null;
  if (multipleOf) {
    const multipleString = Number(multipleOf).toFixed(10);
    const decimalPart = multipleString.split(".")[1];
    if (decimalPart) {
      decimalPlaces = decimalPart.replace(/0+$/, "").length;
    }
  }
  var isNumberField = (actualFieldType === "number" || actualFieldType === "integer") && !Array.isArray(value) && typeof value !== "object";
  var isDateField = rules.format === "date" || rules.format === "date-time" || get(field, "ui.format", "") === "date" || get(field, "ui.format", "") === "date-time";
  var placeholder = actualFieldType === "boolean"
    ? "Select"
    : (isNestedObject ? "" : (examples.length > 0 ? `e.g. ${examples[0]}` : ""));
  var currentLength = typeof value === "string" ? value.length : 0;
  var currentUtf8Length = typeof value === "string" ? new TextEncoder().encode(value).length : 0;
  var isTooLong = (maxLength && currentLength > maxLength) || (maxUtf8ByteLength && currentUtf8Length > maxUtf8ByteLength);
  var numericValue = Number(value);
  var isNumberInvalid = isNumberField && value !== "" && (
    (minimum !== undefined && numericValue < minimum) ||
    (maximum !== undefined && numericValue > maximum) ||
    (exclusiveMinimum !== undefined && numericValue <= exclusiveMinimum) ||
    (exclusiveMaximum !== undefined && numericValue >= exclusiveMaximum)
  );
  var isDateInvalid = false;
  if (isDateField && value) {
    const parsedDate = new Date(value);
    isDateInvalid = isNaN(parsedDate.getTime());
  }
  var isTooShort = !!minLength && currentLength < minLength && currentLength > 0;

  // ============================================================
  //const field detection
  // ============================================================
  var constValue = get(field, "schema.rules.const", undefined);
  var isConstField = constValue !== undefined;

  var inputStyle = {
    width: "100%", fontSize: 12, padding: "4px 8px", borderRadius: 6,
    border: isTooLong || isTooShort || isNumberInvalid || isDateInvalid ? "1px solid red" : "1px solid #ccc",
    background: "var(--color-background-primary, #ffffff)", color: "#333", cursor: "text"
  };
  var plainTextStyle = { width: "100%", fontSize: 12, padding: "4px 0px", color: "#555", wordBreak: "break-word" };

  const getDisplayText = (val) => {
    if (val === null || val === undefined) return "—";
    const strVal = String(val).trim();
    return strVal === "" ? "—" : strVal;
  };

  const handleStandardChange = (newVal) => {
    if (!isEditable) return;
    setValue(newVal);
    if (onFieldChange) onFieldChange({ field: field.field, value: newVal });
  };

  const handleDeepChange = (path, newVal) => {
    if (!isEditable) return;
    const updatedObject = setDeep(value, path, newVal);
    setValue(updatedObject);
    if (onFieldChange) onFieldChange({ field: field.field, value: updatedObject });
  };

  const triggerFieldChange = (updatedValue) => {
    setValue(updatedValue);
    if (onFieldChange) onFieldChange({ field: field.field, value: updatedValue });
  };

  useEffect(() => {
    let newRaw = isUpdated ? changedValue : getInitialValue(get(field, "normalized_value", null));
    if (newRaw === null || newRaw === undefined || newRaw === "") {
      const defaultVal = get(field, "schema.default_value", null);
      if (defaultVal !== null && defaultVal !== undefined) newRaw = defaultVal;
    }
    if (isNestedObject && (newRaw !== null && newRaw !== undefined && newRaw !== "") && !Array.isArray(newRaw) && typeof newRaw !== "object") {
      const itemProps = get(field, "schema.nested_schema.items.properties", null);
      if (itemProps) {
        const structuredObj = {};
        Object.keys(itemProps).forEach(k => {
          if (k === "marketplace_id" || k === "language_tag") return;
          const propSchema = itemProps[k];
          if (k === "value") {
            structuredObj[k] = newRaw;
          } else if (propSchema.type === "object" && propSchema.properties) {
            const childObj = {};
            Object.keys(propSchema.properties).forEach(ck => {
              if (ck !== "marketplace_id" && ck !== "language_tag") childObj[ck] = "";
            });
            structuredObj[k] = childObj;
          } else {
            structuredObj[k] = "";
          }
        });
        newRaw = [structuredObj];
      }
    }
    if (isNestedObject && newRaw !== null && newRaw !== undefined && newRaw !== "" && !Array.isArray(newRaw) && typeof newRaw === "object") {
      newRaw = [newRaw];
    }
    if ((newRaw === null || newRaw === undefined || newRaw === "") && isNestedObject) {
      const itemProps = get(field, "schema.nested_schema.items.properties", null);
      if (itemProps) {
        const emptyObj = {};
        Object.keys(itemProps).forEach(k => {
          if (k === "marketplace_id" || k === "language_tag") return;
          emptyObj[k] = buildEmptyStub(itemProps[k]);
        });
        newRaw = [emptyObj];
      }
    }
    setValue(newRaw);
  }, [field, changedValue, isUpdated]);

  // ============================================================
  //const field → locked display (before other renders)
  // ============================================================
  if (isConstField) {
    return (
      <div style={{ width: "100%", display: "flex", alignItems: "center", gap: 6 }}>
        <div style={{ ...plainTextStyle, flex: 1 }}>{String(constValue)}</div>
        <span style={{
          fontSize: 10, padding: "1px 6px", borderRadius: 4,
          background: "#f3f4f6", color: "#6b7280", border: "1px solid #e5e7eb", whiteSpace: "nowrap"
        }}>
          Fixed
        </span>
      </div>
    );
  }
  if (readOnly && !isNestedObject) {
    return <div style={plainTextStyle}>{renderModalColValue(initial)}</div>;
  }
  if (isSuggestiveField) {
    const filteredOptions = options.filter(opt =>
      String(opt).toLowerCase().includes(String(value || "").toLowerCase())
    );
    return (
      <div style={{ width: "100%", position: "relative" }}>
        <input
          style={{ ...inputStyle, background: "#fff", color: "#111" }}
          value={value !== null && value !== undefined ? String(value) : ""}
          onFocus={() => setShowSuggestions(true)}
          onBlur={() => { setTimeout(() => setShowSuggestions(false), 150); }}
          onChange={(e) => { handleStandardChange(e.target.value); setShowSuggestions(true); }}
          placeholder={placeholder}
          autoComplete="off"
        />
        {/*pattern validation */}
        <PatternValidationHint value={value} rules={rules} />
        {/* deprecated warning */}
        <DeprecatedWarningBadge value={value} deprecatedValues={get(field, "schema.deprecated_values", [])} />
        {/*maxLength/maxUtf8 validation for suggestive single field */}
        {(() => {

          const len =
            typeof value === "string"
              ? value.length
              : 0;

          const utf8len =
            typeof value === "string"
              ? new TextEncoder().encode(value).length
              : 0;

          const hasMaxLength =
            maxLength !== undefined &&
            maxLength !== null &&
            maxLength !== "";

          const hasUtf8 =
            !hasMaxLength &&
            maxUtf8ByteLength !== undefined &&
            maxUtf8ByteLength !== null &&
            maxUtf8ByteLength !== "";

          const tooLong =
            (hasMaxLength && len > Number(maxLength)) ||
            (hasUtf8 && utf8len > Number(maxUtf8ByteLength));

          if (!hasMaxLength && !hasUtf8) return null;

          return (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginTop: 2
              }}
            >
              <div>
                {tooLong && (
                  <small
                    style={{
                      color: "#dc2626",
                      fontSize: 11
                    }}
                  >
                    {hasMaxLength
                      ? `Maximum ${maxLength} characters allowed`
                      : `Maximum ${maxUtf8ByteLength} UTF8 bytes allowed`}
                  </small>
                )}
              </div>

              <small
                style={{
                  color: tooLong ? "#dc2626" : "#6b7280",
                  fontSize: 11
                }}
              >
                {hasMaxLength
                  ? `${len} / ${maxLength}`
                  : `${utf8len} / ${maxUtf8ByteLength} bytes`}
              </small>
            </div>
          );
        })()}
        {showSuggestions && filteredOptions.length > 0 && (
          <div style={{
            position: "absolute", top: "100%", left: 0, right: 0, background: "#fff",
            border: "1px solid #d5d9d9", borderRadius: "8px", marginTop: "2px",
            maxHeight: "220px", overflowY: "auto", zIndex: 9999, boxShadow: "0 2px 8px rgba(0,0,0,0.15)"
          }}>
            {filteredOptions.slice(0, 50).map((opt) => (
              <div key={String(opt)}
                onMouseDown={() => { handleStandardChange(String(opt)); setShowSuggestions(false); }}
                style={{ padding: "10px 12px", cursor: "pointer", fontSize: "12px", background: "#fff", color: "#111" }}
                onMouseEnter={(e) => { e.target.style.background = "#f3f3f3"; }}
                onMouseLeave={(e) => { e.target.style.background = "#fff"; }}
              >
                {String(opt)}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  if (isToggle) {
    const toggleValue = value === true || value === "true" || value === "Yes" || value === "yes";
    if (!isEditable) {
      return <div style={plainTextStyle}>{toggleValue ? "Yes" : "No"}</div>;
    }
    return (
      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <label style={{ position: "relative", display: "inline-block", width: "38px", height: "22px", marginBottom: 0, cursor: "pointer" }}>
          <input type="checkbox" checked={toggleValue} onChange={(e) => handleStandardChange(e.target.checked)}
            style={{ opacity: 0, width: 0, height: 0 }} />
          <span style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: toggleValue ? "#0073bb" : "#c4c4c4", borderRadius: "34px", transition: "0.2s" }}>
            <span style={{ position: "absolute", height: "18px", width: "18px", left: toggleValue ? "18px" : "2px", bottom: "2px", backgroundColor: "white", borderRadius: "50%", transition: "0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.3)" }} />
          </span>
        </label>
        <span style={{ fontSize: "12px", color: "#111827", minWidth: "24px" }}>{toggleValue ? "Yes" : "No"}</span>
      </div>
    );
  }
  if (isPlainMultiTextField) {
    const currentValues = Array.isArray(value) ? value : (value ? [value] : [""]);
    const maxAllowed = rules.maxUniqueItems || rules.maxItems || 99;
    return (
      <div style={{ width: "100%" }}>
        {currentValues.map((item, index) => (
          <div key={index} style={{ position: "relative", marginBottom: "6px", display: "flex", gap: "6px", alignItems: "center" }}>
            <div style={{ flex: 1 }}>
              <AutoResizeTextarea
                style={{ ...inputStyle, width: "100%" }}
                value={item !== null && item !== undefined ? String(item) : ""}
                onChange={(e) => {
                  const updated = [...currentValues];
                  updated[index] = e.target.value;
                  triggerFieldChange(updated);
                }}
                placeholder={placeholder}
              />
              {/* maxLength / maxUtf8 per-item counter */}
              {(() => {
                const len = typeof item === "string" ? item.length : 0;
                const utf8len = typeof item === "string" ? new TextEncoder().encode(item).length : 0;
                const hasMaxLength = maxLength !== undefined && maxLength !== null && maxLength !== "";
                const hasUtf8 = !hasMaxLength && maxUtf8ByteLength !== undefined && maxUtf8ByteLength !== null && maxUtf8ByteLength !== "";
                const tooLong = (hasMaxLength && len > Number(maxLength)) || (hasUtf8 && utf8len > Number(maxUtf8ByteLength));
                if (!hasMaxLength && !hasUtf8) return null;
                return (
                  <div style={{ display: "flex", justifyContent: "space-between", marginTop: 2 }}>
                    <div>
                      {tooLong && (
                        <small style={{ color: "#dc2626", fontSize: 11 }}>
                          {hasMaxLength ? `Maximum ${maxLength} characters allowed` : `Maximum ${maxUtf8ByteLength} UTF8 bytes allowed`}
                        </small>
                      )}
                    </div>
                    <small style={{ color: tooLong ? "#dc2626" : "#6b7280", fontSize: 11 }}>
                      {hasMaxLength ? `${len} / ${maxLength}` : `${utf8len} / ${maxUtf8ByteLength} bytes`}
                    </small>
                  </div>
                );
              })()}
            </div>
            {currentValues.length > 1 && (
              <img
                src={DeleteIcon}
                alt="Delete"
                height={20}
                width={20}
                style={{
                  display: "inline-block",
                  width: "20px",
                  height: "24px",
                  backgroundColor: "#e72424",
                  maskImage: `url(${DeleteIcon})`,
                  WebkitMaskImage: `url(${DeleteIcon})`,
                  maskRepeat: "no-repeat",
                  WebkitMaskRepeat: "no-repeat",
                  maskPosition: "center",
                  WebkitMaskPosition: "center",
                  maskSize: "contain",
                  WebkitMaskSize: "contain",
                  cursor: "pointer",
                }}
                title="Delete"
                onClick={() => triggerFieldChange(currentValues.filter((_, i) => i !== index))}
              />
            )}
          </div>
        ))}
        {currentValues.length < maxAllowed && (
          <span
            style={{ color: "#007185", cursor: "pointer", fontSize: "12px" }}
            onClick={() => triggerFieldChange([...currentValues, ""])}
          >
            + Add More
          </span>
        )}
        {(() => {
          const filled = currentValues.filter(v => v !== "" && v !== null && v !== undefined);
          const hasDup = new Set(filled.map(v => String(v).trim().toLowerCase())).size !== filled.length;
          return hasDup ? (
            <div style={{ color: "#dc2626", fontSize: "11px", marginTop: "4px" }}>
              Duplicate values are not allowed
            </div>
          ) : null;
        })()}
      </div>
    );
  }
  if (isSuggestiveMultiField) {
    const currentValues = Array.isArray(value) ? value : (value ? [value] : [""]);
    const maxAllowed = rules.maxUniqueItems || rules.maxItems || 99;
    return (
      <div style={{ width: "100%" }}>
        {currentValues.map((item, index) => {
          const filtered = options.filter(opt =>
            String(opt).toLowerCase().includes(String(item || "").toLowerCase())
          );
          return (
            <div key={index} style={{ position: "relative", marginBottom: "6px", display: "flex", gap: "6px", alignItems: "center" }}>
              <div style={{ flex: 1, position: "relative" }}>
                <input
                  style={{ ...inputStyle, background: "#fff", color: "#111" }}
                  value={item !== null && item !== undefined ? String(item) : ""}
                  onChange={(e) => {
                    const updated = [...currentValues];
                    updated[index] = e.target.value;
                    triggerFieldChange(updated);
                  }}
                  onFocus={(e) => { const list = e.target.parentNode.querySelector(".sug-list"); if (list) list.style.display = "block"; }}
                  onBlur={(e) => { const parent = e.target.parentNode; setTimeout(() => { const list = parent.querySelector(".sug-list"); if (list) list.style.display = "none"; }, 150); }}
                  placeholder={placeholder || `e.g. ${options[0] || ""}`}
                  autoComplete="off"
                />
                {/* maxLength/maxUtf8 per item validation */}
                {/* Character Counter */}
                {(() => {
                  const len =
                    typeof item === "string" ? item.length : 0;

                  const utf8len =
                    typeof item === "string"
                      ? new TextEncoder().encode(item).length
                      : 0;

                  const hasMaxLength =
                    maxLength !== undefined &&
                    maxLength !== null &&
                    maxLength !== "";

                  const hasUtf8 =
                    !hasMaxLength &&
                    maxUtf8ByteLength !== undefined &&
                    maxUtf8ByteLength !== null &&
                    maxUtf8ByteLength !== "";

                  const tooLong =
                    (hasMaxLength && len > Number(maxLength)) ||
                    (hasUtf8 && utf8len > Number(maxUtf8ByteLength));

                  if (!hasMaxLength && !hasUtf8) return null;

                  return (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginTop: 2
                      }}
                    >
                      <div>
                        {tooLong && (
                          <small
                            style={{
                              color: "#dc2626",
                              fontSize: 11
                            }}
                          >
                            {hasMaxLength
                              ? `Maximum ${maxLength} characters allowed`
                              : `Maximum ${maxUtf8ByteLength} UTF8 bytes allowed`}
                          </small>
                        )}
                      </div>

                      <small
                        style={{
                          color: tooLong ? "#dc2626" : "#6b7280",
                          fontSize: 11
                        }}
                      >
                        {hasMaxLength
                          ? `${len} / ${maxLength}`
                          : `${utf8len} / ${maxUtf8ByteLength} bytes`}
                      </small>
                    </div>
                  );
                })()}
                {filtered.length > 0 && (
                  <div className="sug-list" style={{
                    display: "none", position: "absolute", top: "100%", left: 0, right: 0,
                    background: "#fff", border: "1px solid #d5d9d9", borderRadius: "8px",
                    marginTop: "2px", maxHeight: "180px", overflowY: "auto", zIndex: 9999,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.15)"
                  }}>
                    {filtered.slice(0, 50).map((opt) => (
                      <div key={String(opt)}
                        onMouseDown={() => {
                          const updated = [...currentValues];
                          updated[index] = String(opt);
                          triggerFieldChange(updated);
                        }}
                        style={{ padding: "8px 12px", cursor: "pointer", fontSize: "12px" }}
                        onMouseEnter={(e) => { e.target.style.background = "#f3f3f3"; }}
                        onMouseLeave={(e) => { e.target.style.background = "#fff"; }}
                      >
                        {String(opt)}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {currentValues.length > 1 && (
                <img
                  src={DeleteIcon}
                  alt="Delete"
                  height={20}
                  width={20}
                  style={{
                    display: "inline-block",
                    width: "20px",
                    height: "24px",
                    backgroundColor: "#e72424",
                    maskImage: `url(${DeleteIcon})`,
                    WebkitMaskImage: `url(${DeleteIcon})`,
                    maskRepeat: "no-repeat",
                    WebkitMaskRepeat: "no-repeat",
                    maskPosition: "center",
                    WebkitMaskPosition: "center",
                    maskSize: "contain",
                    WebkitMaskSize: "contain",
                    cursor: "pointer",
                  }}
                  title="Delete"
                  onClick={() => triggerFieldChange(currentValues.filter((_, i) => i !== index))}
                />
              )}
            </div>
          );
        })}
        {currentValues.length < maxAllowed && (
          <span style={{ color: "#007185", cursor: "pointer", fontSize: "12px" }}
            onClick={() => triggerFieldChange([...currentValues, ""])}>
            + Add More
          </span>
        )}
        {(() => {
          const filled = currentValues.filter(v => v !== "" && v !== null && v !== undefined);
          const hasDup = new Set(filled.map(v => String(v).trim().toLowerCase())).size !== filled.length;
          return hasDup ? (
            <div style={{ color: "#dc2626", fontSize: "11px", marginTop: "4px" }}>
              Duplicate values are not allowed
            </div>
          ) : null;
        })()}
      </div>
    );
  }
  if (isDropdown && isMultiValueField) {

    const currentValues = Array.isArray(value)
      ? value
      : value
        ? [value]
        : [""];
    const normalizedValues = currentValues
      .filter(v => v !== null && v !== undefined && v !== "")
      .map(v => String(v).trim().toLowerCase());

    const hasDuplicateValues =
      new Set(normalizedValues).size !== normalizedValues.length;
    return (
      <div style={{ width: "100%" }}>
        {currentValues.map((item, index) => (
          <div
            key={index}
            style={{
              display: "flex",
              gap: "8px",
              marginBottom: "6px",
              alignItems: "center"
            }}
          >
            <select
              style={inputStyle}
              value={item || ""}
              onChange={(e) => {
                const updated = [...currentValues];
                updated[index] = e.target.value;

                setValue(updated);

                if (onFieldChange) {
                  onFieldChange({
                    field: field.field,
                    value: updated
                  });
                }
              }}
            >
              <option value="">
                {placeholder || "Select"}
              </option>

              {options.map((opt, optIndex) => {
                const optionLabels =
                  get(field, "schema.allowed_value_labels", []);

                const label =
                  optionLabels[optIndex] || String(opt);

                return (
                  <option
                    key={String(opt)}
                    value={String(opt)}
                  >
                    {label}
                  </option>
                );
              })}
            </select>

            {currentValues.length > 1 && (
              <button
                type="button"
                onClick={() => {
                  const updated =
                    currentValues.filter((_, i) => i !== index);

                  setValue(updated);

                  if (onFieldChange) {
                    onFieldChange({
                      field: field.field,
                      value: updated
                    });
                  }
                }}
              >
                Remove
              </button>
            )}
          </div>
        ))}

        <span
          style={{
            color: "#007185",
            cursor: "pointer",
            fontSize: "12px"
          }}
          onClick={() => {

            const updated = [...currentValues, ""];

            setValue(updated);

            if (onFieldChange) {
              onFieldChange({
                field: field.field,
                value: updated
              });
            }
          }}
        >
          Add More
        </span>
        {hasDuplicateValues && (
          <div
            style={{
              color: "#dc2626",
              fontSize: "11px",
              marginTop: "4px"
            }}
          >
            Duplicate values are not allowed
          </div>
        )}
      </div>
    );
  }
  if (isDropdown && !isMultiValueField) {
    let selectedValue = "";
    if (value !== null && value !== undefined) {
      selectedValue = Array.isArray(value) ? (value.length > 0 ? String(value[0]) : "") : String(value);
    }
    if (!isEditable) {
      return <div style={plainTextStyle}>{getDisplayText(selectedValue)}</div>;
    }
    return (
      <div>
        <select style={inputStyle} value={selectedValue}
          onChange={(e) => {
            if (!isEditable) return;
            const originalIsArray = Array.isArray(get(field, "normalized_value", null));
            const newVal = originalIsArray ? [e.target.value] : e.target.value;
            setValue(newVal);
            if (onFieldChange) onFieldChange({ field: field.field, value: newVal });
          }}
        >
          <option value="">{placeholder || "Select"}</option>
          {options.map((opt, index) => {
            const optionLabels = get(field, "schema.allowed_value_labels", []);
            const label = optionLabels[index] ? optionLabels[index] : String(opt);
            return <option key={String(opt)} value={String(opt)}>{label}</option>;
          })}
        </select>
        {/*deprecated warning on dropdown */}
        <DeprecatedWarningBadge value={selectedValue} deprecatedValues={get(field, "schema.deprecated_values", [])} />
      </div>
    );
  }

  const isEnumField = (schema = {}) => Array.isArray(schema.enum);
  const isValueUnitField = (schema = {}) => {
    const props = schema.properties || (schema.items && schema.items.properties) || {};
    if (props.value && props.unit) return true;
    // unit_count style: value + type{nested enum}
    if (props.value && props.type && props.type.properties && Array.isArray(props.type.properties.value && props.type.properties.value.enum)) return true;
    return false;
  };
  const resolveEnumFromSchema = (schema = {}) => {
    if (Array.isArray(schema.enum)) {
      return { enum: schema.enum, enumNames: schema.enumNames || [] };
    }
    if (Array.isArray(schema.anyOf)) {
      const enumEntry = schema.anyOf.find(s => Array.isArray(s.enum));
      if (enumEntry) {
        return { enum: enumEntry.enum, enumNames: enumEntry.enumNames || [] };
      }
    }
    if (Array.isArray(schema.oneOf)) {
      const enumEntry = schema.oneOf.find(s => Array.isArray(s.enum));
      if (enumEntry) {
        return { enum: enumEntry.enum, enumNames: enumEntry.enumNames || [] };
      }
    }
    return { enum: [], enumNames: [] };
  };
  const isPrimitiveField = (schema = {}) => {
    if (["string", "number", "integer", "boolean"].includes(schema.type)) return true;
    const variants = schema.oneOf || schema.anyOf;
    if (Array.isArray(variants) && variants.length > 0) {
      return variants.every(v => v && (
        v.format === "date" || v.format === "date-time" ||
        ["string", "number", "integer", "boolean"].includes(v.type)
      ));
    }
    return false;
  };
  const isDateSchema = (schema = {}) => {
    // direct format
    if (
      schema.format === "date" ||
      schema.format === "date-time"
    ) {
      return true;
    }

    // oneOf support
    if (Array.isArray(schema.oneOf)) {
      return schema.oneOf.some(function (item) {
        return item && (
          item.format === "date" ||
          item.format === "date-time"
        );
      });
    }

    // anyOf support
    if (Array.isArray(schema.anyOf)) {
      return schema.anyOf.some(function (item) {
        return item && (
          item.format === "date" ||
          item.format === "date-time"
        );
      });
    }

    // nested object value support
    if (
      schema.properties &&
      schema.properties.value &&
      (
        schema.properties.value.format === "date" ||
        schema.properties.value.format === "date-time"
      )
    ) {
      return true;
    }

    if (
      schema.properties &&
      schema.properties.value &&
      Array.isArray(schema.properties.value.oneOf)
    ) {
      return schema.properties.value.oneOf.some(function (item) {
        return item && (
          item.format === "date" ||
          item.format === "date-time"
        );
      });
    }

    return false;
  };

  const renderDeep = (data, schema = {}, path = [], isObjProp = false, isOfferRoot = false, parentTitle = "") => {
    const schemaIsArray = schema.type === "array" && schema.items;
    const schemaIsObject = schema.type === "object" && schema.properties;
    const schemaIsDate = isDateSchema(schema);
    const schemaIsEnum = isEnumField(schema);
    if ((data === null || data === undefined) && !schemaIsArray && !schemaIsObject && !schemaIsDate && !schemaIsEnum && isEditable && !readOnly) {
      data = "";
    }
    if (data === null || data === undefined) {
      if (!isEditable || readOnly) {
        return <div style={{ ...plainTextStyle, width: isObjProp ? "60%" : "100%" }}>—</div>;
      }
      if (schema.type === "array" && schema.items) {
        return renderDeep([buildEmptyStub(schema.items)], schema, path);
      }
      if (schema.type === "object" && schema.properties) {
        return renderDeep(buildEmptyStub(schema), schema, path);
      }
      const localType = schema.type;
      const localIsNumber = localType === "number" || localType === "integer";
      const localIsDate = isDateSchema(schema);
      const localIsEnum = isEnumField(schema);

      if (localIsDate) {
        return (
          <input type="date" style={{ ...inputStyle, width: "100%", marginBottom: 0 }}
            value=""
            onChange={(e) => handleDeepChange(path, e.target.value)} />
        );
      }
      if (localIsEnum) {
        const { enum: enumOpts, enumNames: enumLabels } = resolveEnumFromSchema(schema);
        return (
          <select style={inputStyle} value=""
            onChange={(e) => handleDeepChange(path, e.target.value)}>
            <option value="">Select</option>
            {enumOpts.map((opt, i) => (
              <option key={String(opt)} value={String(opt)}>
                {enumLabels[i] || String(opt)}
              </option>
            ))}
          </select>
        );
      }
      if (localIsNumber || isNumberField) {
        const undefMin = schema.minimum;
        const undefMax = schema.maximum;
        return (
          <div style={{ width: isObjProp ? "60%" : "100%" }}>
            <AutoResizeTextarea style={{ ...inputStyle, width: "100%", marginBottom: 0 }} value=""
              onChange={(e) => {
                let newValue = e.target.value;
                const numberRegex = localType === "integer" ? /^\d*$/ : /^\d*\.?\d*$/;
                if (newValue !== "" && !numberRegex.test(newValue)) return;
                handleDeepChange(path, newValue);
              }}
              placeholder={placeholder} />
            {(undefMin !== undefined || undefMax !== undefined) && (
              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 2 }}>
                <small style={{ color: "#6b7280", fontSize: 11 }}>
                  {undefMin !== undefined && undefMax !== undefined && `${undefMin} – ${undefMax}`}
                  {undefMin !== undefined && undefMax === undefined && `Min: ${undefMin}`}
                  {undefMin === undefined && undefMax !== undefined && `Max: ${undefMax}`}
                </small>
              </div>
            )}
          </div>
        );
      }
      return (
        <AutoResizeTextarea style={{ ...inputStyle, width: "100%", marginBottom: 0 }} value=""
          onChange={(e) => handleDeepChange(path, e.target.value)} placeholder={placeholder} />
      );
    }
    if (typeof data !== "object") {
      const localFieldType = schema.type;
      const wrappedTitle = get(schema, "properties.value.title", null);
      if (!isEditable || readOnly) {
        return (
          <div style={{ width: isObjProp ? "60%" : "100%" }}>
            {wrappedTitle && <div className="mb-1 fw-bold">{wrappedTitle}</div>}
            <div style={plainTextStyle}>{getDisplayText(data)}</div>
          </div>
        );
      }
      const textValue = data !== null && data !== undefined ? String(data) : "";
      const localIsNumberField = (localFieldType === "number" || localFieldType === "integer");
      const localIsDateField = isDateSchema(schema);

      const localMaxLength = schema.maxLength !== undefined ? schema.maxLength : maxLength;
      const localMinLength = schema.minLength !== undefined ? schema.minLength : minLength;
      const localMaxUtf8 = schema.maxUtf8ByteLength !== undefined ? schema.maxUtf8ByteLength : maxUtf8ByteLength;

      const localPlaceholder = schema.examples && schema.examples.length > 0
        ? `e.g. ${schema.examples[0]}`
        : placeholder;
      const currentLength = textValue.length;
      const currentUtf8Length = new TextEncoder().encode(textValue).length;
      const isTooLong = !localIsNumberField && (
        (localMaxLength && currentLength > localMaxLength) ||
        (localMaxUtf8 && currentUtf8Length > localMaxUtf8)
      );
      const numericValue = Number(textValue);
      // ============================================================
      // local min/max from schema (for nested value fields like list_price, non_lithium_battery_energy_content)
      // ============================================================
      const localMinimum = schema.minimum !== undefined ? schema.minimum : minimum;
      const localMaximum = schema.maximum !== undefined ? schema.maximum : maximum;
      const localExclusiveMin = schema.exclusiveMinimum !== undefined ? schema.exclusiveMinimum : exclusiveMinimum;
      const localExclusiveMax = schema.exclusiveMaximum !== undefined ? schema.exclusiveMaximum : exclusiveMaximum;
      const isNumberInvalid = localIsNumberField && textValue !== "" && (
        (localMinimum !== undefined && numericValue < localMinimum) || (localMaximum !== undefined && numericValue > localMaximum) ||
        (localExclusiveMin !== undefined && numericValue <= localExclusiveMin) || (localExclusiveMax !== undefined && numericValue >= localExclusiveMax)
      );
      // ============================================================
      //compute localDecimalPlaces from schema.multipleOf (nested fields)
      // Falls back to outer decimalPlaces if schema has no multipleOf
      // ============================================================
      let localDecimalPlaces = decimalPlaces; // outer scope fallback
      if (schema.multipleOf !== undefined) {
        const ms = Number(schema.multipleOf).toFixed(10);
        const dp = ms.split(".")[1];
        localDecimalPlaces = dp ? dp.replace(/0+$/, "").length : 0;
      }
      return (
        <div style={{ width: isObjProp ? "60%" : "100%" }}>
          {wrappedTitle && <div className="mb-1 fw-bold">{wrappedTitle}</div>}
          {localIsDateField ? (
            <input type="date" style={{ ...inputStyle, width: "100%", marginBottom: 0 }}
              value={textValue ? textValue.split("T")[0] : ""}
              onChange={(e) => handleDeepChange(path, e.target.value)} />
          ) : (
            options.length > 0 ? (
              <>
                <input list={`suggestions-${field.field}`} style={{ ...inputStyle, width: "100%", marginBottom: 0 }}
                  value={textValue}
                  onFocus={(e) => { const input = e.target; const current = input.value; input.value = ""; setTimeout(() => { input.value = current; }, 0); }}
                  onChange={(e) => { handleDeepChange(path, e.target.value); }}
                  placeholder={localPlaceholder} autoComplete="off" />
                <datalist id={`suggestions-${field.field}`}>
                  {options.map((opt) => <option key={String(opt)} value={String(opt)} />)}
                </datalist>
              </>
            ) : (
              <AutoResizeTextarea
                style={{ ...inputStyle, width: "100%", marginBottom: 0, border: (isTooLong || (isTooShort && !localIsNumberField) || isNumberInvalid) ? "1px solid red" : "1px solid #ccc" }}
                value={textValue}
                onChange={(e) => {
                  let newValue = e.target.value;
                  if (localIsNumberField) {
                    //use localDecimalPlaces (from schema.multipleOf) not outer decimalPlaces
                    const numberRegex = localFieldType === "integer" ? /^\d*$/ : (localDecimalPlaces === null ? /^\d*\.?\d*$/ : new RegExp(`^\\d*\\.?\\d{0,${localDecimalPlaces}}$`));
                    if (newValue !== "" && !numberRegex.test(newValue)) return;
                  }
                  handleDeepChange(path, newValue);
                }}
                placeholder={localPlaceholder} />
            )
          )}
          {/*pattern/URL validation in renderDeep */}
          <PatternValidationHint value={textValue} rules={schema} />
          {!localIsNumberField &&
            (localMaxLength || localMaxUtf8 || localMinLength) && (() => {

              const hasMaxLength =
                localMaxLength !== undefined &&
                localMaxLength !== null &&
                localMaxLength !== "";

              const hasUtf8 =
                !hasMaxLength &&
                localMaxUtf8 !== undefined &&
                localMaxUtf8 !== null &&
                localMaxUtf8 !== "";

              const tooLong =
                (hasMaxLength && currentLength > Number(localMaxLength)) ||
                (hasUtf8 && currentUtf8Length > Number(localMaxUtf8));

              return (
                <div className="d-flex justify-content-between">

                  <div>
                    {tooLong && (
                      <small className="text-danger">
                        {hasMaxLength
                          ? `Maximum ${localMaxLength} characters allowed`
                          : `Maximum ${localMaxUtf8} UTF8 bytes allowed`}
                      </small>
                    )}

                    {isTooShort && (
                      <small className="text-danger">
                        Minimum {localMinLength} characters required
                      </small>
                    )}
                  </div>

                  <small className={tooLong ? "text-danger" : "text-muted"}>
                    {hasMaxLength
                      ? `${currentLength} / ${localMaxLength}`
                      : `${currentUtf8Length} / ${localMaxUtf8} bytes`}
                  </small>

                </div>
              );
            })()}
          {/*range hint for nested number fields (list_price.value, non_lithium_battery_energy_content.value etc.) */}
          {localIsNumberField && (localMinimum !== undefined || localMaximum !== undefined) && !isNumberInvalid && (
            <div className="d-flex justify-content-end mt-1">
              <small className="text-muted">
                {localMinimum !== undefined && localMaximum !== undefined && <>{localMinimum} {"–"} {localMaximum}</>}
                {localMinimum !== undefined && localMaximum === undefined && <>Min: {localMinimum}</>}
                {localMinimum === undefined && localMaximum !== undefined && <>Max: {localMaximum}</>}
              </small>
            </div>
          )}
          {isNumberInvalid && (
            <div className="d-flex justify-content-end">
              {localExclusiveMin !== undefined && numericValue <= localExclusiveMin && <small className="text-danger">Value must be greater than {localExclusiveMin}</small>}
              {localExclusiveMax !== undefined && numericValue >= localExclusiveMax && <small className="text-danger">Value must be less than {localExclusiveMax}</small>}
              {localMinimum !== undefined && localMaximum !== undefined && localExclusiveMin === undefined && localExclusiveMax === undefined && <small className="text-danger">Value must be between {localMinimum} {"–"} {localMaximum}</small>}
              {localMinimum !== undefined && localMaximum === undefined && localExclusiveMin === undefined && <small className="text-danger">Value must be at least {localMinimum}</small>}
              {localMinimum === undefined && localMaximum !== undefined && localExclusiveMax === undefined && <small className="text-danger">Maximum value is {localMaximum}</small>}
            </div>
          )}
        </div>
      );
    }

    if (Array.isArray(data)) {
      const normalizedValues = data.filter(item => item !== null && item !== undefined && item !== "").map(item => JSON.stringify(item));
      const hasDuplicateValues = new Set(normalizedValues).size !== normalizedValues.length;
      const maxAllowedItems =
  schema.maxItems !== undefined ? schema.maxItems :
  schema.maxUniqueItems !== undefined ? schema.maxUniqueItems :
  1;
      const minAllowedItems = schema.minItems !== undefined ? schema.minItems : 1;

      const removeItemAt = (removeIndex) => {
        const updated = data.filter((_, i) => i !== removeIndex);
        if (path.length === 0) {
          setValue(updated);
          if (onFieldChange) onFieldChange({ field: field.field, value: updated });
        } else {
          triggerFieldChange(setDeep(value, path, updated));
        }
      };

      const addItem = () => {
        const newItem = buildEmptyStub(schema.items);
        const updated = [...data, newItem];
        if (path.length === 0) {
          setValue(updated);
          if (onFieldChange) onFieldChange({ field: field.field, value: updated });
        } else {
          triggerFieldChange(setDeep(value, path, updated));
        }
      };

      return (
        <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>
          {data.map((item, index) => {
            const isPrimitive = typeof item !== "object" || item === null;
            const isCompactObject = item && typeof item === "object" && Object.keys(item).length <= 3;
            let itemSchema = schema.items ? schema.items : schema;
            if (isPrimitive && itemSchema.type === "object" && itemSchema.properties) {
              const numProp = Object.entries(itemSchema.properties).find(
                ([k, v]) => (v.type === "number" || v.type === "integer") && !v.hidden
              );
              if (numProp) {
                itemSchema = numProp[1];
              } else {
                const enumProp = Object.entries(itemSchema.properties).find(
                  ([k, v]) => k !== "marketplace_id" && k !== "language_tag" && Array.isArray(v.enum)
                );
                if (enumProp) {
                  itemSchema = enumProp[1];
                }
              }
            }

            const unwrappedItem = (isPrimitive && itemSchema.type === "object" && itemSchema.properties)
              ? (() => {
                const valueKey = Object.keys(itemSchema.properties).find(
                  k => k !== "marketplace_id" && k !== "language_tag"
                );
                return valueKey ? { [valueKey]: item } : item;
              })()
              : item;
            const unwrappedPath = (isPrimitive && unwrappedItem !== item)
              ? [...path, index]
              : [...path, index];

            const canDelete = isEditable && data.length > minAllowedItems;

            const deleteBtn = canDelete ? (
              <img
                src={DeleteIcon}
                alt="Delete"
                height={20}
                width={20}
                style={{
                  display: "inline-block",
                  width: "20px",
                  height: "24px",
                  backgroundColor: "#e72424",
                  maskImage: `url(${DeleteIcon})`,
                  WebkitMaskImage: `url(${DeleteIcon})`,
                  maskRepeat: "no-repeat",
                  WebkitMaskRepeat: "no-repeat",
                  maskPosition: "center",
                  WebkitMaskPosition: "center",
                  maskSize: "contain",
                  WebkitMaskSize: "contain",
                  cursor: "pointer",
                  flexShrink: 0
                }}
                title="Delete"
                onClick={() => removeItemAt(index)}
              />
            ) : null;

            if (!isPrimitive) {
              const isTopLevelEntry = !!(schema.selectors && schema.selectors.length > 0);

              const varyingSelectors = isTopLevelEntry
                ? schema.selectors.filter((s) => {
                    const seen = new Set(
                      data
                        .filter((d) => d && typeof d === "object" && d[s] !== undefined && d[s] !== null && d[s] !== "")
                        .map((d) => String(d[s]))
                    );
                    return seen.size >= 1;
                  })
                : [];

              const selectorLabel = isTopLevelEntry
                ? varyingSelectors
                    .filter(s => item && item[s] !== undefined && item[s] !== null && item[s] !== "")
                    .map(s => {
                      const propSchema = get(itemSchema, `properties.${s}`, {});
                      const idx = Array.isArray(propSchema.enum) ? propSchema.enum.indexOf(item[s]) : -1;
                      const displayVal = idx !== -1 && propSchema.enumNames ? propSchema.enumNames[idx] : item[s];
                      return `${toReadableLabel(s)}: ${displayVal}`;
                    })
                    .join("  ·  ")
                : null;

              if (isTopLevelEntry) {
                const allowedAudiences = getAllowedValuesForKey(itemSchema, "audience");
                if (allowedAudiences && item && item.audience !== undefined && !allowedAudiences.includes(item.audience)) {
                  return null;
                }
              }

              if (!isTopLevelEntry && isOfferGroupField) {
                return (
                  <div key={index} style={{
                    display: "flex", gap: "8px", alignItems: "flex-start",
                    paddingBottom: data.length > 1 && index < data.length - 1 ? 8 : 0,
                    borderBottom: data.length > 1 && index < data.length - 1 ? "1px solid #e5e7eb" : "none"
                  }}>
                    <div style={{ flex: 1 }}>
                      {renderDeep(unwrappedItem, itemSchema, unwrappedPath)}
                    </div>
                    {deleteBtn}
                  </div>
                );
              }

              if (!isTopLevelEntry) {
                const isPassthroughItem = isPassthroughWrapperSchema(itemSchema);
                if (isPassthroughItem) {
                  return (
                    <div key={index} style={{ display: "flex", gap: "8px", alignItems: "flex-start", marginBottom: 8 }}>
                      <div style={{ flex: 1 }}>
                        {renderDeep(unwrappedItem, itemSchema, unwrappedPath)}
                      </div>
                      {deleteBtn}
                    </div>
                  );
                }
                return (
                  <div key={index} style={{ display: "flex", gap: "8px", alignItems: "flex-start", marginBottom: 8 }}>
                    <div style={{ flex: 1, border: "1px solid #e5e7eb", borderRadius: 6, padding: "10px 12px", background: "#fafafa" }}>
                      {renderDeep(unwrappedItem, itemSchema, unwrappedPath)}
                    </div>
                    {deleteBtn}
                  </div>
                );
              }

              return (
                <div key={index} style={{ display: "flex", gap: "8px", alignItems: "flex-start", marginBottom: 8 }}>
                  <div style={{ flex: 1, border: "1px solid #e5e7eb", borderRadius: 6, padding: "10px 12px", background: "#fafafa" }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#111827", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.03em" }}>
                    {selectorLabel}
                  </div>
                    {renderDeep(unwrappedItem, itemSchema, unwrappedPath, false, true)}
                  </div>
                  {deleteBtn}
                </div>
              );
            }

            return (
              <div key={index} style={{
                display: "flex", gap: "8px", alignItems: "center",
                marginBottom: (isPrimitive || isCompactObject) ? "4px" : (index < data.length - 1 ? "8px" : "0")
              }}>
                <div style={{ flex: 1 }}>
                  {renderDeep(unwrappedItem, itemSchema, unwrappedPath)}
                </div>
                {deleteBtn}
              </div>
            );
          })}

          {isEditable && data.length < maxAllowedItems && (
            <span style={{ color: "#007185", cursor: "pointer", fontSize: "12px" }} onClick={addItem}>
              + Add More
            </span>
          )}
          {hasDuplicateValues && <div className="text-danger" style={{ fontSize: "11px", marginTop: "4px" }}>Duplicate values are not allowed</div>}
        </div>
      );
    }

    if (typeof data === "object" && !Array.isArray(data)) {
      const objectProperties = schema.properties || get(schema, "items.properties", {});

      if (isValueUnitField(schema)) {
        const props = schema.properties || (schema.items && schema.items.properties) || {};
        const hasTypeWrapper = !props.unit && !!(props.type && props.type.properties);
        const valSchema = props.value;
        const unitSchema = hasTypeWrapper ? props.type.properties.value : props.unit;
        const valExamples = get(schema, "properties.value.examples", []);
        const valPlaceholder = valExamples.length > 0 ? `e.g. ${valExamples[0]}` : "";
        const isNumVal = valSchema.type === "number" || valSchema.type === "integer";
        const vRaw = data && data.value !== undefined ? String(data.value) : "";
        const uRaw = data && data.unit !== undefined ? String(data.unit) : "";
        const vNum = Number(vRaw);
        const vMin = valSchema.minimum;
        const vMax = valSchema.maximum;
        const vExMin = valSchema.exclusiveMinimum;
        const vExMax = valSchema.exclusiveMaximum;
        const unitMissing = vRaw.trim() !== "" && uRaw.trim() === "";
        const valueMissing = uRaw.trim() !== "" && vRaw.trim() === "";
        const vInvalid = isNumVal && vRaw !== "" && (
          (vMin !== undefined && vNum < vMin) || (vMax !== undefined && vNum > vMax) ||
          (vExMin !== undefined && vNum <= vExMin) || (vExMax !== undefined && vNum >= vExMax)
        );
        let vDecimalPlaces = null;
        if (valSchema.multipleOf !== undefined) {
          const ms = Number(valSchema.multipleOf).toFixed(10);
          const dp = ms.split(".")[1];
          vDecimalPlaces = dp ? dp.replace(/0+$/, "").length : 0;
        }
        return (
          <div style={{ border: "1px solid #e5e7eb", borderRadius: 6, padding: "10px 12px", background: "#fafafa" }}>
            <div className="mb-1">
              {valSchema.title || schema.title || "Value"}
              {(valSchema.description || schema.description) && (
                <span
                  title={valSchema.description || schema.description}
                  style={{ cursor: "pointer", marginLeft: 6, color: "#6c757d" }}
                >
                  <FontAwesomeIcon icon={faInfoCircle} />
                </span>
              )}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
              <div>
                <input
                  type="text"
                  style={{ ...inputStyle, border: (vInvalid || valueMissing) ? "1px solid red" : "1px solid #ccc" }}
                  placeholder={valPlaceholder}
                  value={vRaw}
                  onChange={(e) => {
                    let v = e.target.value;
                    if (isNumVal && v !== "") {
                      const numRegex = valSchema.type === "integer"
                        ? /^\d*$/
                        : vDecimalPlaces === null
                          ? /^\d*\.?\d*$/
                          : new RegExp(`^\\d*\\.?\\d{0,${vDecimalPlaces}}$`);
                      if (!numRegex.test(v)) return;
                    }
                    handleDeepChange([...path, "value"], v);
                  }}
                />
                {vInvalid && (
                  <small style={{ color: "#dc2626", fontSize: 11, display: "block", marginTop: 2 }}>
                    {vExMin !== undefined && vNum <= vExMin && `Value must be greater than ${vExMin}`}
                    {vExMax !== undefined && vNum >= vExMax && `Value must be less than ${vExMax}`}
                    {vMin !== undefined && vMax !== undefined && vExMin === undefined && vExMax === undefined && `Value must be between ${vMin} – ${vMax}`}
                    {vMin !== undefined && vMax === undefined && vExMin === undefined && `Value must be at least ${vMin}`}
                    {vMin === undefined && vMax !== undefined && vExMax === undefined && `Value must be between 0 – ${vMax}`}
                  </small>
                )}
                {!vInvalid && (vMin !== undefined || vMax !== undefined) && (
                  <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 2 }}>
                    <small style={{ color: "#6b7280", fontSize: 11 }}>
                      {vMin !== undefined && vMax !== undefined && `${vMin} – ${vMax}`}
                      {vMin !== undefined && vMax === undefined && `Min: ${vMin}`}
                      {vMin === undefined && vMax !== undefined && `0 – ${vMax}`}
                    </small>
                  </div>
                )}
                {!isNumVal && (() => {
                  const len = vRaw.length;
                  const utf8len = new TextEncoder().encode(vRaw).length;
                  const vMaxLen = valSchema.maxLength;
                  const vMaxUtf8 = valSchema.maxUtf8ByteLength;
                  const hasML = vMaxLen !== undefined && vMaxLen !== null;
                  const hasUtf8 = !hasML && vMaxUtf8 !== undefined && vMaxUtf8 !== null;
                  const tooLong = (hasML && len > Number(vMaxLen)) || (hasUtf8 && utf8len > Number(vMaxUtf8));
                  if (!hasML && !hasUtf8) return null;
                  return (
                    <div style={{ display: "flex", justifyContent: "space-between", marginTop: 2 }}>
                      <div>{tooLong && <small style={{ color: "#dc2626", fontSize: 11 }}>{hasML ? `Maximum ${vMaxLen} characters allowed` : `Maximum ${vMaxUtf8} UTF8 bytes allowed`}</small>}</div>
                      <small style={{ color: tooLong ? "#dc2626" : "#6b7280", fontSize: 11 }}>{hasML ? `${len} / ${vMaxLen}` : `${utf8len} / ${vMaxUtf8} bytes`}</small>
                    </div>
                  );
                })()}
              </div>

              <select
                style={{ ...inputStyle, border: unitMissing ? "1px solid red" : "1px solid #ccc", alignSelf: "flex-start" }}
                value={uRaw}
                onChange={(e) => handleDeepChange(hasTypeWrapper ? [...path, "type", "value"] : [...path, "unit"], e.target.value)}
              >
                <option value="">Select Unit</option>
                {(() => {
                  const { enum: unitEnums, enumNames: unitLabels } = resolveEnumFromSchema(unitSchema);
                  return unitEnums.map((unit, index) => (
                    <option key={unit} value={unit}>
                      {unitLabels[index] ? unitLabels[index] : unit}
                    </option>
                  ));
                })()}
              </select>
            </div>
          </div>
        );
      
      }
      const propEntries = Object.entries(objectProperties);
      const renderField = ([key, propertySchema]) => {
            if (key === "marketplace_id" || key === "language_tag") return null;
            const fieldValue = data && data[key] !== undefined ? data[key] : undefined;
            const isHidden = propertySchema.hidden === true;
            if (isHidden) return null;
            const currentPath = [...path, key];
            const isEditableField = propertySchema.editable !== false;
            if (!isEditableField) return <div style={plainTextStyle}>{String(fieldValue || "-")}</div>;
            const isBooleanEnumField = propertySchema.type === "boolean" || (Array.isArray(propertySchema.enum) && propertySchema.enum.every(item => typeof item === "boolean"));
            if (isBooleanEnumField) {
              const toggleValue = fieldValue === true;
              return (
                <div key={key}>
                  <div className="mb-1">
                    {propertySchema.title || key}
                    {propertySchema.description && (
                      <span title={propertySchema.description} style={{ cursor: "pointer", marginLeft: "6px", color: "#6c757d" }}>
                        <FontAwesomeIcon icon={faInfoCircle} />
                      </span>
                    )}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <label style={{ position: "relative", display: "inline-block", width: "38px", height: "22px", marginBottom: 0, cursor: "pointer" }}>
                      <input type="checkbox" checked={toggleValue} onChange={(e) => { const updated = setDeep(value, currentPath, e.target.checked); triggerFieldChange(updated); }} style={{ opacity: 0, width: 0, height: 0 }} />
                      <span style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: toggleValue ? "#0073bb" : "#c4c4c4", borderRadius: "34px", transition: "0.2s" }}>
                        <span style={{ position: "absolute", height: "18px", width: "18px", left: toggleValue ? "18px" : "2px", bottom: "2px", backgroundColor: "white", borderRadius: "50%", transition: "0.2s" }} />
                      </span>
                    </label>
                    <span style={{ fontSize: "12px" }}>{toggleValue ? "Enabled" : "Disabled"}</span>
                  </div>
                </div>
              );
            }
            if (isEnumField(propertySchema)) {
              const enumOptions = propertySchema.enum || [];
              const finalOptions = (fieldValue && !enumOptions.includes(fieldValue)) ? [fieldValue, ...enumOptions] : enumOptions;
              return (
                <div key={key}>
                  <div className="mb-1">
                    {propertySchema.title || key}
                    {propertySchema.description && (
                      <span title={propertySchema.description} style={{ cursor: "pointer", marginLeft: "6px", color: "#6c757d" }}>
                        <FontAwesomeIcon icon={faInfoCircle} />
                      </span>
                    )}
                  </div>
                  <select style={inputStyle} value={fieldValue !== undefined && fieldValue !== null ? String(fieldValue) : ""}
                    onChange={(e) => {
                      let selectedValue = e.target.value;
                      if (propertySchema.type === "boolean") {
                        if (selectedValue === "true") selectedValue = true;
                        else if (selectedValue === "false") selectedValue = false;
                      }
                      triggerFieldChange(setDeep(value, currentPath, selectedValue));
                    }}>
                    <option value="">Select</option>
                    {finalOptions.map((opt, index) => {
                      const optionLabel = propertySchema.enumNames && propertySchema.enumNames[index] ? propertySchema.enumNames[index] : String(opt);
                      return <option key={String(opt)} value={propertySchema.type === "boolean" ? String(opt) : opt}>{optionLabel}</option>;
                    })}
                  </select>
                </div>
              );
            }
            const isArrayEnumField = (schema = {}) =>
              schema.type === "array" && schema.items && Array.isArray(schema.items.enum);
            if (isArrayEnumField(propertySchema)) {
              const enumOptions = propertySchema.items.enum || [];
              const enumNames = propertySchema.items.enumNames || [];
              const maxAllowed = propertySchema.maxItems || propertySchema.maxUniqueItems || 99;
              const minAllowed = propertySchema.minItems || 1;
              const currentVals = Array.isArray(fieldValue)
                ? fieldValue
                : fieldValue
                  ? [fieldValue]
                  : [""];

              const filledVals = currentVals.filter(
                (v) => v !== "" && v !== null && v !== undefined
              );
              const hasDup =
                new Set(filledVals.map((v) => String(v).trim().toLowerCase())).size !==
                filledVals.length;

                return (
                  <div key={key}>
                    <div className="mb-1">
                      {propertySchema.title || key}
                      {propertySchema.description && (
                        <span title={propertySchema.description} style={{ cursor: "pointer", marginLeft: "6px", color: "#6c757d" }}>
                          <FontAwesomeIcon icon={faInfoCircle} />
                        </span>
                      )}
                    </div>
                    {currentVals.map((item, index) => (
                    <div
                      key={index}
                      style={{
                        display: "flex",
                        gap: "6px",
                        marginBottom: "6px",
                        alignItems: "center",
                      }}
                    >
                      <select
                        style={inputStyle}
                        value={item || ""}
                        onChange={(e) => {
                          const updated = [...currentVals];
                          updated[index] = e.target.value;
                          triggerFieldChange(setDeep(value, currentPath, updated));
                        }}
                      >
                        <option value="">Select</option>
                        {enumOptions.map((opt, i) => (
                          <option key={String(opt)} value={String(opt)}>
                            {enumNames[i] || String(opt)}
                          </option>
                        ))}
                      </select>
                      {currentVals.length > minAllowed && (
                        <img
                          src={DeleteIcon}
                          alt="Delete"
                          height={20}
                          width={20}
                          style={{
                            display: "inline-block",
                            width: "20px",
                            height: "24px",
                            backgroundColor: "#e72424",
                            maskImage: `url(${DeleteIcon})`,
                            WebkitMaskImage: `url(${DeleteIcon})`,
                            maskRepeat: "no-repeat",
                            WebkitMaskRepeat: "no-repeat",
                            maskPosition: "center",
                            WebkitMaskPosition: "center",
                            maskSize: "contain",
                            WebkitMaskSize: "contain",
                            cursor: "pointer",
                          }}
                          title="Delete"
                          onClick={() => {
                            const updated = currentVals.filter((_, i) => i !== index);
                            triggerFieldChange(setDeep(value, currentPath, updated));
                          }}
                        />
                      )}
                    </div>
                  ))}
                  {currentVals.length < maxAllowed && (
                    <span
                      style={{ color: "#007185", cursor: "pointer", fontSize: "12px" }}
                      onClick={() =>
                        triggerFieldChange(setDeep(value, currentPath, [...currentVals, ""]))
                      }
                    >
                      + Add More
                    </span>
                  )}
                  {hasDup && (
                    <div style={{ color: "#dc2626", fontSize: "11px", marginTop: "4px" }}>
                      Duplicate values are not allowed
                    </div>
                  )}
                </div>
              );
            }
            if (isValueUnitField(propertySchema)) {
              const isArrayWrapped = propertySchema.type === "array";
              const nestedProps = propertySchema.properties || (propertySchema.items && propertySchema.items.properties) || {};
              const valSchema = nestedProps.value;
              const unitSchema = nestedProps.unit;
              const valExamples = valSchema.examples || [];
              const valPlaceholder = valExamples.length > 0 ? `e.g. ${valExamples[0]}` : "";
              const isNumVal = valSchema.type === "number" || valSchema.type === "integer";
              const actualValueObj = isArrayWrapped ? (Array.isArray(fieldValue) ? fieldValue[0] : undefined) : fieldValue;
              const vRaw = actualValueObj && actualValueObj.value !== undefined ? String(actualValueObj.value) : "";
              const uRaw = actualValueObj && actualValueObj.unit !== undefined ? String(actualValueObj.unit) : "";
              const vNum = Number(vRaw);
              const vMin = valSchema.minimum;
              const vMax = valSchema.maximum;
              const vExMin = valSchema.exclusiveMinimum;
              const vExMax = valSchema.exclusiveMaximum;
              // value filled but unit empty → unit red
              const unitMissing = vRaw.trim() !== "" && uRaw.trim() === "";
              const valueMissing = uRaw.trim() !== "" && vRaw.trim() === "";
              // number range invalid
              const vInvalid = isNumVal && vRaw !== "" && (
                (vMin !== undefined && vNum < vMin) ||
                (vMax !== undefined && vNum > vMax) ||
                (vExMin !== undefined && vNum <= vExMin) ||
                (vExMax !== undefined && vNum >= vExMax)
              );
              // decimal places from multipleOf
              let vDecimalPlaces = null;
              if (valSchema.multipleOf !== undefined) {
                const ms = Number(valSchema.multipleOf).toFixed(10);
                const dp = ms.split(".")[1];
                vDecimalPlaces = dp ? dp.replace(/0+$/, "").length : 0;
              }

              return (
                <div key={key} style={{ border: "1px solid #e5e7eb", borderRadius: 6, padding: "10px 12px", background: "#fafafa" }}>
                  <div className="mb-1">
                    {propertySchema.title || key}
                    {propertySchema.description && (
                      <span title={propertySchema.description} style={{ cursor: "pointer", marginLeft: "6px", color: "#6c757d" }}>
                        <FontAwesomeIcon icon={faInfoCircle} />
                      </span>
                    )}
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
                    <div>
                      <input
                        type="text"
                        style={{ ...inputStyle, border: (vInvalid || valueMissing) ? "1px solid red" : "1px solid #ccc" }}
                        placeholder={valPlaceholder}
                        value={vRaw}
                        onChange={(e) => {
                          let v = e.target.value;
                          if (isNumVal && v !== "") {
                            const numRegex = valSchema.type === "integer"
                              ? /^\d*$/
                              : vDecimalPlaces === null
                                ? /^\d*\.?\d*$/
                                : new RegExp(`^\\d*\\.?\\d{0,${vDecimalPlaces}}$`);
                            if (!numRegex.test(v)) return;
                          }
                          triggerFieldChange(setDeep(value, isArrayWrapped ? [...currentPath, 0, "value"] : [...currentPath, "value"], v));
                        }}
                      />
                      {vInvalid && (
                        <small style={{ color: "#dc2626", fontSize: 11, display: "block", marginTop: 2 }}>
                          {vExMin !== undefined && vNum <= vExMin && `Value must be greater than ${vExMin}`}
                          {vExMax !== undefined && vNum >= vExMax && `Value must be less than ${vExMax}`}
                          {vMin !== undefined && vMax !== undefined && vExMin === undefined && vExMax === undefined && `Value must be between ${vMin} – ${vMax}`}
                          {vMin !== undefined && vMax === undefined && vExMin === undefined && `Value must be at least ${vMin}`}
                          {vMin === undefined && vMax !== undefined && vExMax === undefined && `Value must be between 0 – ${vMax}`}
                        </small>
                      )}
                      {/* range hint when valid */}
                      {!vInvalid && (vMin !== undefined || vMax !== undefined) && (
                        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 2 }}>
                          <small style={{ color: "#6b7280", fontSize: 11 }}>
                            {vMin !== undefined && vMax !== undefined && `${vMin} – ${vMax}`}
                            {vMin !== undefined && vMax === undefined && `Min: ${vMin}`}
                            {vMin === undefined && vMax !== undefined && `0 – ${vMax}`}
                          </small>
                        </div>
                      )}
                      {/* maxLength / maxUtf8 counter */}
                      {!isNumVal && (() => {
                        const len = vRaw.length;
                        const utf8len = new TextEncoder().encode(vRaw).length;
                        const vMaxLen = valSchema.maxLength;
                        const vMaxUtf8 = valSchema.maxUtf8ByteLength;
                        const hasML = vMaxLen !== undefined && vMaxLen !== null;
                        const hasUtf8 = !hasML && vMaxUtf8 !== undefined && vMaxUtf8 !== null;
                        const tooLong = (hasML && len > Number(vMaxLen)) || (hasUtf8 && utf8len > Number(vMaxUtf8));
                        if (!hasML && !hasUtf8) return null;
                        return (
                          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 2 }}>
                            <div>{tooLong && <small style={{ color: "#dc2626", fontSize: 11 }}>{hasML ? `Maximum ${vMaxLen} characters allowed` : `Maximum ${vMaxUtf8} UTF8 bytes allowed`}</small>}</div>
                            <small style={{ color: tooLong ? "#dc2626" : "#6b7280", fontSize: 11 }}>{hasML ? `${len} / ${vMaxLen}` : `${utf8len} / ${vMaxUtf8} bytes`}</small>
                          </div>
                        );
                      })()}
                    </div>
                    {/* {valueMissing && (
                    <small style={{ color: "#dc2626", fontSize: 11, display: "block", marginTop: 2 }}>
                      Please enter a value
                    </small>
                  )} */}
                    <select
                      style={{ ...inputStyle, border: unitMissing ? "1px solid red" : "1px solid #ccc", alignSelf: "flex-start" }}
                      value={uRaw}
                      onChange={(e) => triggerFieldChange(setDeep(value, isArrayWrapped ? [...currentPath, 0, "unit"] : [...currentPath, "unit"], e.target.value))}
                    >
                      <option value="">Select Unit</option>
                      {(() => {
                        const { enum: unitEnums, enumNames: unitLabels } = resolveEnumFromSchema(unitSchema);
                        return unitEnums.map((unit, index) => (
                          <option key={unit} value={unit}>
                            {unitLabels[index] ? unitLabels[index] : unit}
                          </option>
                        ));
                      })()}
                    </select>
                  </div>
                  {/* {unitMissing && (
                    <small style={{ color: "#dc2626", fontSize: 11, display: "block", marginTop: 2 }}>
                      Please select a unit
                    </small>
                  )} */}
                </div>
              );
            }
            if (isPrimitiveField(propertySchema)) {
              const isDateField = isDateSchema(propertySchema);
              const rawTitle = propertySchema.title || key;
              const displayTitle = isRedundantTitle(rawTitle, parentTitle) ? toReadableLabel(key) : rawTitle;
              return (
                <div key={key}>
                  <div className="mb-1 fw-bold">
                    {displayTitle}
                    {propertySchema.description && (
                      <span title={propertySchema.description} style={{ cursor: "pointer", marginLeft: "6px", color: "#6c757d" }}>
                        <FontAwesomeIcon icon={faInfoCircle} />
                      </span>
                    )}
                  </div>
                  {isDateField ? (
                    <input type="date" style={inputStyle} value={fieldValue ? String(fieldValue).split("T")[0] : ""}
                      onChange={(e) => triggerFieldChange(setDeep(value, currentPath, e.target.value))} />
                  ) : (
                    renderDeep(fieldValue, propertySchema, currentPath)
                  )}
                </div>
              );
            }


            const isPassthrough = isPassthroughWrapperSchema(propertySchema);
            const skipTitle = isOfferGroupField || isPassthrough;
            const rawTitle = propertySchema.title || toReadableLabel(key);
            const isDuplicate = isRedundantTitle(rawTitle, parentTitle);
            return (
              <div key={key}>
                {!skipTitle && (
                  <div className="mb-1 fw-bold">
                    {isDuplicate ? toReadableLabel(key) : rawTitle}
                  </div>
                )}
                {renderDeep(fieldValue, propertySchema, currentPath, false, false, rawTitle)}
              </div>
            );
      };

     

      return (
        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          {propEntries.map(renderField)}
        </div>
      );
    }
  };

  if (Array.isArray(value) || (value !== null && typeof value === "object")) {
    if (!isNestedObject) {
      const nestedSchema = get(field, "schema.nested_schema", {});
      return renderDeep(value, nestedSchema);
    }

    const nestedSchema = get(field, "schema.nested_schema", {});
    const resolvedFieldLabel = fieldLabel ||
      get(field, "schema.title", null) ||
      get(field, "render.label", null) ||
      toReadableLabel(field.field) || "Details";
    const isDirty = JSON.stringify(value) !== modalInitialValue;
    const summary = getSummary(value);

    const handleModalSave = () => {
      setShowNestedModal(false);
    };

    const handleModalCancel = () => {
      if (modalInitialValue !== null) {
        const restored = JSON.parse(modalInitialValue);
        setValue(restored);
        if (onFieldChange && !readOnly) {
          onFieldChange({ field: field.field, value: restored });
        }
      }
      setShowNestedModal(false);
    };

    return (
      <>
        {/* Inline preview row */}
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{
            flex: 1, fontSize: 12, color: "#6b7280",
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
            background: "#ffffff", border: "1px solid #e5e7eb",
            borderRadius: 6, padding: "4px 8px"
          }}>
            {summary}
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              setModalInitialValue(JSON.stringify(value)); // ← snapshot
              setShowNestedModal(true);
            }}
            style={{
              background: "none", border: "1px solid #0073bb",
              color: "#0073bb", borderRadius: 6, padding: "3px 10px",
              fontSize: 12, cursor: "pointer", whiteSpace: "nowrap"
            }}
          >
            {readOnly ? "View" : "Edit"}
          </button>
        </div>

        {/* Modal */}
        {showNestedModal && (
          <div
            onClick={(e) => { if (e.target === e.currentTarget) handleModalCancel(); }}
            style={{
              position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
              background: "rgba(0,0,0,0.5)", zIndex: 99999,
              display: "flex", alignItems: "center", justifyContent: "center",paddingTop: "70px",
              paddingBottom: "20px", // <-- Keep centered!
            }}
          >

            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: "#fff", borderRadius: 10,
                width: 1224, maxWidth: "95vw",
                maxHeight: "calc(100vh - 90px)",
                display: "flex", flexDirection: "column",
                boxShadow: "0 8px 32px rgba(0,0,0,0.2)"
              }}
            >
              {/* Header */}
              <div style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "14px 18px", borderBottom: "1px solid #e5e7eb", flexShrink: 0
              }}>
                <span style={{ fontSize: 14, fontWeight: 600, color: "#111" }}>
                  {resolvedFieldLabel}
                </span>
                <button onClick={handleModalCancel} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 20, color: "#6b7280", lineHeight: 1, padding: "0 4px" }}>×</button>
              </div>
              <div style={{ overflowY: "auto", flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
                <div style={{
                  display: "grid", gridTemplateColumns: "1fr 1fr 1fr",
                  borderBottom: "1px solid #e5e7eb",
                  background: "#f9fafb",
                  position: "sticky", top: 0, zIndex: 10, flexShrink: 0
                }}>
                  {["Catalog", "Listing", "Edit"].map((col) => (
                    <div key={col} style={{
                      padding: "8px 12px", fontSize: 11, fontWeight: 600,
                      color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.04em",
                      borderRight: col !== "Edit" ? "1px solid #e5e7eb" : "none",
                      minWidth: 0
                    }}>
                      {col}
                    </div>
                  ))}
                </div>

                {/* Content Area */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", flex: 1 }}>

                 {/* Catalog column */}
                 <div style={{ padding: "16px 12px", borderRight: "1px solid #e5e7eb", fontSize: 12, color: "#374151", wordBreak: "break-word", whiteSpace: "pre-wrap", minWidth: 0 }}>
                    {renderModalColValue(filterAudienceEntries(get(field, "catalog.value", null), nestedSchema), nestedSchema, resolvedFieldLabel)}
                  </div>

                  {/* Listing column */}
                  <div style={{ padding: "16px 12px", borderRight: "1px solid #e5e7eb", fontSize: 12, color: "#374151", wordBreak: "break-word", whiteSpace: "pre-wrap", minWidth: 0 }}>
                    {renderModalColValue(filterAudienceEntries(get(field, "listing.value", null), nestedSchema), nestedSchema, resolvedFieldLabel)}
                  </div>

                  {/* Edit column (Removed inner overflowY: "auto") */}
                  <div style={{ padding: "16px 12px", minWidth: 0 }}>
                    {renderDeep(value, nestedSchema)}
                  </div>

                </div>

              </div>

              {/* Footer */}
              <div style={{
                padding: "12px 18px", borderTop: "1px solid #e5e7eb",
                display: "flex", gap: 8, justifyContent: "flex-end", flexShrink: 0
              }}>
                <button
                  onClick={handleModalCancel}
                  style={{
                    background: "#fff", border: "1px solid #d1d5db",
                    color: "#374151", borderRadius: 6,
                    padding: "7px 16px", fontSize: 13, cursor: "pointer"
                  }}
                >
                  Cancel
                </button>
                {!readOnly && (
                  <button
                    onClick={handleModalSave}
                    disabled={!isDirty}
                    style={{
                      background: isDirty ? "#0073bb" : "#93c5fd",
                      border: "none", color: "#fff",
                      borderRadius: 6, padding: "7px 16px",
                      fontSize: 13, fontWeight: 500,
                      cursor: isDirty ? "pointer" : "not-allowed"
                    }}
                  >
                    Save Changes
                  </button>)}
              </div>
            </div>
          </div>
        )}
      </>
    );

  }

  if (!isEditable) {
    return <div style={plainTextStyle}>{getDisplayText(value)}</div>;
  }

  return (
    <div style={{ width: "100%" }}>
      {isDateField ? (
        <input type="date" style={inputStyle} value={value ? String(value).split("T")[0] : ""}
          onChange={(e) => handleStandardChange(e.target.value)} />
      ) : (
        <AutoResizeTextarea style={inputStyle}
          value={value !== null && value !== undefined ? String(value) : ""}
          onChange={(e) => {
            let newValue = e.target.value;
            if (isNumberField) {
              const numberRegex = actualFieldType === "integer" ? /^\d*$/ : (decimalPlaces === null ? /^\d*\.?\d*$/ : new RegExp(`^\\d*\\.?\\d{0,${decimalPlaces}}$`));
              if (newValue !== "" && !numberRegex.test(newValue)) return;
            }
            handleStandardChange(newValue);
          }}
          placeholder={placeholder} />
      )}
      {/* pattern/URL inline validation */}
      <PatternValidationHint value={value} rules={rules} />
      {!isNumberField && (maxLength || maxUtf8ByteLength || minLength) && (() => {

        const hasMaxLength =
          maxLength !== undefined &&
          maxLength !== null &&
          maxLength !== "";

        const hasUtf8 =
          !hasMaxLength &&
          maxUtf8ByteLength !== undefined &&
          maxUtf8ByteLength !== null &&
          maxUtf8ByteLength !== "";

        const tooLong =
          (hasMaxLength && currentLength > Number(maxLength)) ||
          (hasUtf8 && currentUtf8Length > Number(maxUtf8ByteLength));

        return (
          <div className="d-flex justify-content-between">

            <div>
              {tooLong && (
                <small className="text-danger">
                  {hasMaxLength
                    ? `Maximum ${maxLength} characters allowed`
                    : `Maximum ${maxUtf8ByteLength} UTF8 bytes allowed`}
                </small>
              )}

              {isTooShort && (
                <small className="text-danger">
                  Minimum {minLength} characters required
                </small>
              )}
            </div>

            <small className={tooLong ? "text-danger" : "text-muted"}>
              {hasMaxLength
                ? `${currentLength} / ${maxLength}`
                : `${currentUtf8Length} / ${maxUtf8ByteLength} bytes`}
            </small>

          </div>
        );
      })()}
      {isNumberField && minimum !== undefined && (actualFieldType === "number" || actualFieldType === "integer") && (
        <div className="d-flex justify-content-end mt-1">
          <small className="text-muted">
            {minimum !== undefined && maximum !== undefined && <>{minimum} - {maximum}</>}
            {minimum !== undefined && maximum === undefined && <>Min: {minimum}</>}
            {minimum === undefined && maximum !== undefined && <>Max: {maximum}</>}
          </small>
        </div>
      )}
      {isNumberInvalid && (
        <div className="d-flex justify-content-end">
          {exclusiveMinimum !== undefined && numericValue <= exclusiveMinimum && <small className="text-danger">Value must be greater than {exclusiveMinimum}</small>}
          {exclusiveMaximum !== undefined && numericValue >= exclusiveMaximum && <small className="text-danger">Value must be less than {exclusiveMaximum}</small>}
          {minimum !== undefined && maximum !== undefined && exclusiveMinimum === undefined && exclusiveMaximum === undefined && <small className="text-danger">Value must be between {minimum} - {maximum}</small>}
          {minimum !== undefined && maximum === undefined && exclusiveMinimum === undefined && <small className="text-danger">Value must be at least {minimum}</small>}
          {minimum === undefined && maximum !== undefined && exclusiveMaximum === undefined && <small className="text-danger">Maximum value is {maximum}</small>}
        </div>
      )}
      {isDateInvalid && (
        <div className="d-flex justify-content-end mt-1">
          <small className="text-danger">Invalid date format (YYYY-MM-DD)</small>
        </div>
      )}
    </div>
  );
}

export default AmazonProductListingEditableField;